import { createContext, useContext, useEffect, useState, useRef } from 'react';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const didLogoutRef = useRef(false); // 🔁 skip refresh after logout

  const isTokenExpired = () => {
    if (!token) return true;
    try {
      const decoded = JSON.parse(atob(token.split('.')[1]));
      const expiration = decoded.exp * 1000;
      return Date.now() >= expiration;
    } catch (err) {
      return true;
    }
  };

  const refreshToken = async () => {
    try {
      const res = await fetch(`${import.meta.env.VITE_BASE_URL}/api/auth/refresh-token`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!res.ok) {
        // Only logout on serious errors, not just token refresh
        return false;
      }
      
      const data = await res.json();
      setToken(data.token);
      localStorage.setItem('token', data.token);
      setLoading(false);
    } catch (err) {
      console.error('Token refresh error:', err);
      logout();
    }
  };
  

  const login = (accessToken) => {
    setToken(accessToken);
    localStorage.setItem('token', accessToken);
  };

  const logout = async () => {
    try {
      await fetch(`${import.meta.env.VITE_BASE_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      console.error('Logout error:', err);
    }
  
    didLogoutRef.current = true;
    setToken(null);
    localStorage.removeItem('token');
  };

  useEffect(() => {
    if (didLogoutRef.current) {
      setLoading(false);                 // ✅ skip token refresh after logout
      didLogoutRef.current = false;
      return;
    }

    if (!token || isTokenExpired()) {
      refreshToken();
    } else {
      setLoading(false);
    }
  }, [token]);

  return (
    <AuthContext.Provider value={{ token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};
